import React, { useEffect, useState, useRef } from 'react';
import { useApp } from '../contexts/AppContext';
import { parseNaturalLanguageDate, VIEW_CONFIG } from '../utils/helpers';
import {
  Inbox,
  Star,
  Calendar,
  Layers,
  Cloud,
  BookOpen,
  Plus,
  Search,
  FolderKanban,
  ArrowRight,
  Sparkles,
  Target,
} from 'lucide-react';
import './CommandPalette.css';

const VIEW_ICONS = {
  inbox: Inbox,
  today: Star,
  upcoming: Calendar,
  anytime: Layers,
  someday: Cloud,
  logbook: BookOpen,
};

export default function CommandPalette() {
  const { state, dispatch, searchTasks } = useApp();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  const isOpen = state.commandPaletteOpen;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        dispatch({ type: 'TOGGLE_COMMAND_PALETTE' });
      }
      if (e.key === 'Escape' && isOpen) {
        dispatch({ type: 'SET_COMMAND_PALETTE', payload: false });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const commands = [];

  // Quick add task
  if (query.trim()) {
    const parsed = parseNaturalLanguageDate(query);
    commands.push({
      id: 'quick-add',
      label: `Add task: "${parsed.title}"`,
      sublabel: parsed.dueDate
        ? `Due: ${new Date(parsed.dueDate).toLocaleDateString()}${parsed.dueTime ? ` at ${parsed.dueTime}` : ''}`
        : null,
      icon: Plus,
      action: () => {
        dispatch({
          type: 'ADD_TASK',
          payload: {
            title: parsed.title,
            dueDate: parsed.dueDate,
            dueTime: parsed.dueTime,
            status: state.activeView || 'inbox',
          },
        });
        dispatch({ type: 'SET_COMMAND_PALETTE', payload: false });
      },
    });
  }

  // App actions
  const actionCommands = [
    {
      id: 'focus-mode',
      label: 'Start focus mode',
      sublabel: 'Just one thing at a time',
      icon: Target,
      action: () => {
        dispatch({ type: 'OPEN_FOCUS_MODE' });
        dispatch({ type: 'SET_COMMAND_PALETTE', payload: false });
      },
    },
    {
      id: 'brain-dump',
      label: 'Brain dump',
      sublabel: 'Empty your head — Molehill sorts it',
      icon: Sparkles,
      action: () => {
        dispatch({ type: 'OPEN_BRAIN_DUMP' });
        dispatch({ type: 'SET_COMMAND_PALETTE', payload: false });
      },
    },
  ];
  actionCommands.forEach((cmd) => {
    if (!query || cmd.label.toLowerCase().includes(query.toLowerCase())) {
      commands.push(cmd);
    }
  });

  // Navigate to views
  Object.entries(VIEW_CONFIG).forEach(([key, config]) => {
    if (!query || config.label.toLowerCase().includes(query.toLowerCase())) {
      commands.push({
        id: `nav-${key}`,
        label: `Go to ${config.label}`,
        icon: VIEW_ICONS[key],
        action: () => {
          dispatch({ type: 'SET_ACTIVE_VIEW', payload: key });
          dispatch({ type: 'SET_COMMAND_PALETTE', payload: false });
        },
      });
    }
  });

  // Navigate to projects
  state.projects.forEach((project) => {
    if (!query || project.name.toLowerCase().includes(query.toLowerCase())) {
      commands.push({
        id: `project-${project.id}`,
        label: `Go to ${project.name}`,
        sublabel: 'Project',
        icon: FolderKanban,
        action: () => {
          dispatch({ type: 'SET_ACTIVE_PROJECT', payload: project.id });
          dispatch({ type: 'SET_COMMAND_PALETTE', payload: false });
        },
      });
    }
  });

  // Search results
  if (query.trim().length >= 2) {
    const results = searchTasks(query);
    results.slice(0, 5).forEach((task) => {
      commands.push({
        id: `task-${task.id}`,
        label: task.title,
        sublabel: `in ${VIEW_CONFIG[task.status]?.label || 'Unknown'}`,
        icon: ArrowRight,
        action: () => {
          if (task.status !== 'logbook') {
            dispatch({ type: 'SET_ACTIVE_VIEW', payload: task.status });
          }
          dispatch({ type: 'SET_SELECTED_TASK', payload: task.id });
          dispatch({ type: 'SET_COMMAND_PALETTE', payload: false });
        },
      });
    });
  }

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, commands.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && commands[selectedIndex]) {
      e.preventDefault();
      commands[selectedIndex].action();
    }
  };

  return (
    <div className="command-palette-overlay" onClick={() => dispatch({ type: 'SET_COMMAND_PALETTE', payload: false })}>
      <div className="command-palette" onClick={(e) => e.stopPropagation()}>
        <div className="command-input-wrapper">
          <Search size={18} className="command-search-icon" />
          <input
            ref={inputRef}
            className="command-input"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Type a command or search..."
          />
          <kbd className="command-shortcut">ESC</kbd>
        </div>

        <div className="command-list">
          {commands.map((cmd, index) => {
            const Icon = cmd.icon;
            return (
              <button
                key={cmd.id}
                className={`command-item ${index === selectedIndex ? 'selected' : ''}`}
                onClick={cmd.action}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <span className="command-icon">
                  <Icon size={16} />
                </span>
                <div className="command-text">
                  <span className="command-label">{cmd.label}</span>
                  {cmd.sublabel && (
                    <span className="command-sublabel">{cmd.sublabel}</span>
                  )}
                </div>
              </button>
            );
          })}
          {commands.length === 0 && (
            <div className="command-empty">No results found</div>
          )}
        </div>

        <div className="command-footer">
          <span><kbd>↑↓</kbd> Navigate</span>
          <span><kbd>↵</kbd> Select</span>
          <span><kbd>esc</kbd> Close</span>
        </div>
      </div>
    </div>
  );
}
